import { useState } from "react";
import { motion } from "framer-motion";
import { Clock, Facebook, Mail, MapPin, Phone, Send } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { CONTACT, SERVICES } from "@/data/content";

const EASE = [0.22, 1, 0.36, 1];
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-xl border border-white/15 bg-white/5 px-5 py-4 text-sm text-blush-50 placeholder:text-blush-50/40 outline-none transition-colors duration-300 focus:border-blush-500";

export default function Booking() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", service: SERVICES[0].name, message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/bookings`, form);
      toast.success("Richiesta inviata! Controlla la tua email: ti abbiamo inviato una conferma.");
      setForm({ name: "", phone: "", email: "", service: SERVICES[0].name, message: "" });
    } catch {
      toast.error("Qualcosa è andato storto. Riprova o chiamaci direttamente.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="prenota" data-testid="booking-section" className="bg-ink py-28 text-blush-50 lg:py-40">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="grid grid-cols-1 gap-20 lg:grid-cols-2">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: EASE }}
          >
            <p className="mb-6 flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.4em] text-blush-500">
              <span className="h-px w-10 bg-blush-500" />
              Prenota
            </p>
            <h2 className="font-serif text-5xl leading-[1.05] sm:text-6xl lg:text-7xl">
              Il tuo momento <br />
              <span className="italic text-blush-500">ti aspetta.</span>
            </h2>
            <p className="mt-8 max-w-md text-base leading-relaxed text-blush-50/70">
              Lasciaci i tuoi contatti e il trattamento che desideri: ti
              richiameremo per fissare l'appuntamento perfetto per te.
            </p>

            <div className="mt-14 space-y-6 text-sm">
              <p className="flex items-center gap-4" data-testid="contact-address">
                <MapPin size={18} className="shrink-0 text-blush-500" /> {CONTACT.address}
              </p>
              <a href={CONTACT.phoneHref} className="flex items-center gap-4 transition-colors duration-300 hover:text-blush-500" data-testid="contact-phone">
                <Phone size={18} className="shrink-0 text-blush-500" /> {CONTACT.phone}
              </a>
              <a href={CONTACT.emailHref} className="flex items-center gap-4 transition-colors duration-300 hover:text-blush-500" data-testid="contact-email">
                <Mail size={18} className="shrink-0 text-blush-500" /> {CONTACT.email}
              </a>
              <a href={CONTACT.facebookHref} target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 transition-colors duration-300 hover:text-blush-500" data-testid="contact-facebook">
                <Facebook size={18} className="shrink-0 text-blush-500" /> {CONTACT.facebook}
              </a>
            </div>

            <div className="mt-14 border-t border-white/10 pt-10" data-testid="opening-hours">
              <p className="mb-5 flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.35em] text-blush-500">
                <Clock size={14} /> Orari di apertura
              </p>
              <div className="space-y-3 text-sm">
                {CONTACT.hours.map((h) => (
                  <div key={h.days} className="flex max-w-sm items-baseline justify-between gap-4">
                    <span className="text-blush-50/70">{h.days}</span>
                    <span className="flex-1 border-b border-dotted border-white/15" />
                    <span className="font-medium">{h.time}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>

          <motion.form
            onSubmit={submit}
            data-testid="booking-form"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.15 }}
            className="h-fit rounded-3xl border border-white/10 bg-white/5 p-8 backdrop-blur-md lg:p-12"
          >
            <div className="space-y-6">
              <div>
                <label htmlFor="bk-name" className="mb-2 block text-[10px] font-semibold uppercase tracking-[0.3em] text-blush-50/60">
                  Nome e cognome
                </label>
                <input id="bk-name" data-testid="booking-name-input" required value={form.name} onChange={set("name")} placeholder="Maria Rossi" className={inputCls} />
              </div>
              <div>
                <label htmlFor="bk-phone" className="mb-2 block text-[10px] font-semibold uppercase tracking-[0.3em] text-blush-50/60">
                  Telefono
                </label>
                <input id="bk-phone" data-testid="booking-phone-input" required type="tel" value={form.phone} onChange={set("phone")} placeholder="+39 333 123 4567" className={inputCls} />
              </div>
              <div>
                <label htmlFor="bk-email" className="mb-2 block text-[10px] font-semibold uppercase tracking-[0.3em] text-blush-50/60">
                  Email
                </label>
                <input id="bk-email" data-testid="booking-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="maria.rossi@email.com" className={inputCls} />
              </div>
              <div>
                <label htmlFor="bk-service" className="mb-2 block text-[10px] font-semibold uppercase tracking-[0.3em] text-blush-50/60">
                  Trattamento
                </label>
                <select id="bk-service" data-testid="booking-service-select" value={form.service} onChange={set("service")} className={`${inputCls} appearance-none`}>
                  {SERVICES.map((s) => (
                    <option key={s.id} value={s.name} className="bg-ink text-blush-50">
                      {s.name} — {s.price}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="bk-msg" className="mb-2 block text-[10px] font-semibold uppercase tracking-[0.3em] text-blush-50/60">
                  Note (facoltativo)
                </label>
                <textarea id="bk-msg" data-testid="booking-message-input" rows={4} value={form.message} onChange={set("message")} placeholder="Preferenze di giorno e orario..." className={`${inputCls} resize-none`} />
              </div>
              <button
                type="submit"
                data-testid="booking-submit-button"
                disabled={sending}
                className="group flex w-full items-center justify-center gap-3 rounded-full bg-blush-500 py-5 text-xs font-semibold uppercase tracking-[0.3em] text-white transition-colors duration-500 hover:bg-blush-600 disabled:opacity-60"
              >
                {sending ? "Invio in corso..." : "Invia richiesta"}
                <Send size={15} className="transition-transform duration-500 group-hover:translate-x-1.5 group-hover:-translate-y-0.5" />
              </button>
            </div>
          </motion.form>
        </div>

        <motion.div
          data-testid="map-section"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.9, ease: EASE }}
          className="mt-24"
        >
          <p className="mb-8 flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.4em] text-blush-500">
            <span className="h-px w-10 bg-blush-500" />
            Dove trovarci
          </p>
          <div className="overflow-hidden rounded-3xl border border-white/10">
            <iframe
              title="Lash Queen — Viale Giosuè Carducci 34/P, Carpi"
              data-testid="google-map-iframe"
              src="https://www.google.com/maps?q=Viale%20Giosu%C3%A8%20Carducci%2034%2FP%2C%2041012%20Carpi%20MO%2C%20Italia&output=embed"
              className="h-[380px] w-full"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </motion.div>

        <div className="mt-28 flex flex-col items-start justify-between gap-8 border-t border-white/10 pt-12 lg:flex-row lg:items-center">
          <p className="font-serif text-3xl italic" data-testid="footer-logo">
            Lash Queen <span className="ml-2 align-middle font-sans text-[10px] font-semibold not-italic uppercase tracking-[0.35em] text-blush-500">Carpi</span>
          </p>
          <p className="text-xs text-blush-50/50">
            © {new Date().getFullYear()} Lash Queen — Centro Estetico, Carpi (MO). Tutti i diritti riservati.
          </p>
        </div>
      </div>
    </section>
  );
}
